// ============================================================================
// IDEATION TAB
// Main tab component for ideation campaign management, submissions, and visualization.
// Redesigned from Stitch designs: campaign cards grid, stats row, enhanced
// bubble chart with quadrant labels, priority distribution bar, and table.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Lightbulb, Loader2, CheckCircle, XCircle, Copy, Check,
  ChevronDown, ChevronRight, ExternalLink, Trash2, X, Archive,
  Upload, Download, FileSpreadsheet, TrendingUp, BarChart3
} from 'lucide-react';
import { ideationService, Campaign, Submission, IdeationScores } from '../../services/ideationService';
import { IdeationSurveyForm } from './IdeationSurveyForm';
import { IdeationBubbleChart } from './IdeationBubbleChart';

interface IdeationTabProps {
  projectId: string;
  token: string;
}

const PRIORITY_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  'Quick Win': { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  'Strategic Initiative': { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-400' },
  'Deprioritise': { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-400' },
  'Revisit Later': { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
};

const PRIORITY_BAR_COLORS: Record<string, string> = {
  'Quick Win': 'bg-emerald-400',
  'Strategic Initiative': 'bg-amber-400',
  'Deprioritise': 'bg-red-400',
  'Revisit Later': 'bg-slate-400',
};

const SCORE_LABEL = ['', 'Low', 'Med', 'High'];

export const IdeationTab: React.FC<IdeationTabProps> = ({ projectId }) => {
  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Campaign creation
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignDesc, setNewCampaignDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Selected campaign
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  // Survey form
  const [showSurvey, setShowSurvey] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastScores, setLastScores] = useState<IdeationScores | null>(null);

  // Submissions table
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | null>(null);
  const [chartSelectedId, setChartSelectedId] = useState<string | null>(null);

  // Clipboard
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Import/Export
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors?: { row: number; error: string }[] } | null>(null);

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [campaignList, submissionList] = await Promise.all([
        ideationService.listCampaigns(projectId),
        ideationService.listSubmissions(projectId)
      ]);
      setCampaigns(campaignList);
      setSubmissions(submissionList);

      if (!selectedCampaignId && campaignList.length > 0) {
        const active = campaignList.find(c => c.status === 'active');
        if (active) setSelectedCampaignId(active.campaignId);
        else setSelectedCampaignId(campaignList[0].campaignId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load ideation data');
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedCampaignId]);

  useEffect(() => { loadData(); }, [loadData]);

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  // ---- Campaign Actions ----

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) return;
    setCreating(true);
    try {
      const result = await ideationService.createCampaign(projectId, {
        name: newCampaignName.trim(),
        description: newCampaignDesc.trim()
      });
      setShowCreateCampaign(false);
      setNewCampaignName('');
      setNewCampaignDesc('');
      setSelectedCampaignId(result.campaignId);
      showSuccess('Campaign created');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCampaign = async (campaignId: string) => {
    try {
      await ideationService.updateCampaign(projectId, campaignId, { status: 'closed' });
      showSuccess('Campaign closed');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to close campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm('Delete this campaign? This cannot be undone.')) return;
    try {
      await ideationService.deleteCampaign(projectId, campaignId);
      if (selectedCampaignId === campaignId) setSelectedCampaignId(null);
      showSuccess('Campaign deleted');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete campaign');
    }
  };

  const handleCopyLink = (campaign: Campaign) => {
    const link = campaign.formUrl || `(Configure n8n form URL for campaign ${campaign.campaignId})`;
    navigator.clipboard.writeText(link);
    setCopiedId(campaign.campaignId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ---- Submission Actions ----

  const handleSubmitIdea = async (answers: Record<string, any>) => {
    if (!selectedCampaignId) {
      setError('Please select or create a campaign first');
      return;
    }
    setSubmitting(true);
    try {
      const result = await ideationService.createSubmission(projectId, {
        campaignId: selectedCampaignId,
        answers,
        submitterName: answers.submitterName,
        submitterTeam: answers.submitterTeam,
        submitterEmail: answers.submitterEmail
      });
      setLastScores(result.scores);
      setShowSurvey(false);
      showSuccess(`Idea submitted — scored as "${result.scores.priorityLabel}"`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to submit idea');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    try {
      await ideationService.deleteSubmission(projectId, submissionId);
      showSuccess('Submission deleted');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete submission');
    }
  };

  // ---- Import / Export ----

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCampaignId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await ideationService.importSubmissions(projectId, selectedCampaignId, file);
      setImportResult({ imported: result.imported, failed: result.failed, errors: result.errors });
      if (result.imported > 0) {
        showSuccess(`Imported ${result.imported} submission${result.imported !== 1 ? 's' : ''}${result.failed > 0 ? ' (' + result.failed + ' failed)' : ''}`);
        await loadData();
      } else {
        setError(`Import failed: no submissions imported. ${result.failed} row${result.failed !== 1 ? 's' : ''} had errors.`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import file');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    try {
      await ideationService.exportSubmissions(projectId, selectedCampaignId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to export submissions');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await ideationService.downloadTemplate(projectId);
    } catch (err: any) {
      setError(err.message || 'Failed to download template');
    }
  };

  // ---- Computed data ----

  const filteredSubmissions = selectedCampaignId
    ? submissions.filter(s => s.campaignId === selectedCampaignId)
    : submissions;

  const selectedCampaign = campaigns.find(c => c.campaignId === selectedCampaignId);

  // Stats
  const totalSubs = filteredSubmissions.length;
  const priorityCounts: Record<string, number> = {};
  filteredSubmissions.forEach(s => {
    priorityCounts[s.priorityLabel] = (priorityCounts[s.priorityLabel] || 0) + 1;
  });
  const avgComposite = totalSubs > 0
    ? (filteredSubmissions.reduce((sum, s) => sum + s.compositeScore, 0) / totalSubs).toFixed(1)
    : '—';

  // ---- Render ----

  if (loading) {
    return (
      <div className="text-center py-10">
        <Loader2 size={24} className="text-primary animate-spin mx-auto mb-3" />
        <p className="text-slate-500 text-sm font-medium">Loading ideation data...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Notifications */}
      {error && (
        <div className="flex items-center justify-between bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 text-sm">
          <div className="flex items-center gap-2"><XCircle size={16} /> {error}</div>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-3 rounded-xl border border-emerald-200 text-sm font-medium">
          <CheckCircle size={16} /> {success}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[#052831] text-2xl font-black tracking-tight">Ideation Campaign Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage and analyze your AI use case ideation campaigns.</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedCampaign && selectedCampaign.status === 'active' && (
            <button
              onClick={() => setShowSurvey(!showSurvey)}
              className="flex items-center gap-2 bg-white border border-slate-200 hover:border-primary hover:text-primary px-4 py-2.5 rounded-xl text-sm font-bold text-slate-700 transition-all"
            >
              <Lightbulb size={16} /> Submit an Idea
            </button>
          )}
          <button
            onClick={() => setShowCreateCampaign(!showCreateCampaign)}
            className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-primary/20 text-sm"
          >
            <Plus size={16} /> New Campaign
          </button>
        </div>
      </div>

      {/* Score Result Card */}
      {lastScores && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="flex items-center gap-4 flex-1">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Latest Submission Result</span>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-4xl font-black text-[#052831] tracking-tighter">{lastScores.compositeScore}</span>
                <span className="text-slate-400 font-medium text-sm">Composite Score</span>
              </div>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-xl text-sm font-bold ${PRIORITY_STYLES[lastScores.priorityLabel]?.bg || ''} ${PRIORITY_STYLES[lastScores.priorityLabel]?.text || ''}`}>
            {lastScores.priorityLabel}
          </span>
          <div className="grid grid-cols-3 gap-6 border-l border-slate-100 pl-6">
            <div className="text-center">
              <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Impact</p>
              <p className="text-sm font-bold text-[#052831]">{SCORE_LABEL[lastScores.impactScore]}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Complexity</p>
              <p className="text-sm font-bold text-[#052831]">{SCORE_LABEL[lastScores.complexityScore]}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Viability</p>
              <p className="text-sm font-bold text-[#052831]">{SCORE_LABEL[lastScores.viabilityScore]}</p>
            </div>
          </div>
          <button onClick={() => setLastScores(null)} className="text-slate-400 hover:text-slate-600 self-start"><X size={14} /></button>
        </div>
      )}

      {/* Campaign Cards Grid */}
      <section>
        {/* Create campaign form */}
        {showCreateCampaign && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Campaign Name *</label>
                <input
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  placeholder="e.g. Q1 2026 AI Ideation"
                  value={newCampaignName}
                  onChange={e => setNewCampaignName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                <input
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  placeholder="Optional description..."
                  value={newCampaignDesc}
                  onChange={e => setNewCampaignDesc(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateCampaign}
                disabled={!newCampaignName.trim() || creating}
                className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Create Campaign
              </button>
              <button
                onClick={() => { setShowCreateCampaign(false); setNewCampaignName(''); setNewCampaignDesc(''); }}
                className="text-sm text-slate-500 hover:text-slate-700 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {campaigns.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm text-center py-12">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <Lightbulb size={24} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No campaigns yet. Create one to start collecting ideas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {campaigns.map(campaign => {
              const isSelected = selectedCampaignId === campaign.campaignId;
              const isActive = campaign.status === 'active';
              const subCount = submissions.filter(s => s.campaignId === campaign.campaignId).length;
              return (
                <div
                  key={campaign.campaignId}
                  onClick={() => setSelectedCampaignId(campaign.campaignId)}
                  className={`bg-white p-5 rounded-xl flex flex-col gap-4 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-2 border-primary shadow-sm'
                      : 'border border-slate-200 hover:border-slate-300 shadow-sm'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                      isActive ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {campaign.status}
                    </span>
                    <div className="flex gap-1 text-slate-400">
                      {campaign.formUrl && (
                        <button
                          onClick={e => { e.stopPropagation(); handleCopyLink(campaign); }}
                          className="hover:text-primary transition-colors p-0.5"
                          title="Copy shareable link"
                        >
                          {copiedId === campaign.campaignId ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                        </button>
                      )}
                      {campaign.formUrl && (
                        <a
                          href={campaign.formUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="hover:text-primary transition-colors p-0.5"
                          title="Open form"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-[#052831] font-bold text-lg leading-tight">{campaign.name}</h3>
                    <p className="text-slate-500 text-sm mt-1">{subCount} Submission{subCount !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="mt-auto pt-4 flex justify-between items-center border-t border-slate-100">
                    {campaign.description ? (
                      <span className="text-[10px] font-bold text-slate-400 uppercase truncate mr-2">{campaign.description}</span>
                    ) : (
                      <span className="text-[10px] font-bold text-slate-400 uppercase">
                        {new Date(campaign.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    <div className="flex gap-1 shrink-0">
                      {isActive && (
                        <button
                          onClick={e => { e.stopPropagation(); handleCloseCampaign(campaign.campaignId); }}
                          className="text-slate-400 hover:text-amber-500 transition-colors"
                          title="Close campaign"
                        >
                          <Archive size={14} />
                        </button>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteCampaign(campaign.campaignId); }}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Delete campaign"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Import/Export Toolbar */}
      {selectedCampaign && (
        <div className="flex flex-wrap items-center justify-between gap-4 py-2 px-1 border-b border-slate-200">
          <div className="flex gap-3">
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 text-slate-600 hover:text-primary px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-sm font-medium transition-all"
            >
              <Download size={14} /> Template
            </button>
            <input ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" onChange={handleImportFile} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-2 bg-primary text-white px-4 py-1.5 rounded-xl text-sm font-bold transition-all shadow-sm disabled:opacity-50"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {importing ? 'Importing...' : 'Import CSV/XLS'}
            </button>
          </div>
          {filteredSubmissions.length > 0 && (
            <button
              onClick={handleExport}
              className="flex items-center gap-2 text-slate-600 hover:text-primary px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-sm font-medium transition-all"
            >
              <FileSpreadsheet size={14} /> Export XLSX
            </button>
          )}
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div className={`rounded-xl px-4 py-3 text-sm ${importResult.failed > 0 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
          <span className="font-bold">Import complete:</span> {importResult.imported} imported{importResult.failed > 0 && `, ${importResult.failed} failed`}
          {importResult.errors && importResult.errors.length > 0 && (
            <div className="mt-1.5 space-y-0.5">
              {importResult.errors.slice(0, 5).map((e) => (
                <div key={String(e.row)} className="text-xs">Row {e.row}: {e.error}</div>
              ))}
              {importResult.errors.length > 5 && (
                <div className="text-xs">...and {importResult.errors.length - 5} more errors</div>
              )}
            </div>
          )}
          <button onClick={() => setImportResult(null)} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Survey Form (expandable) */}
      {showSurvey && selectedCampaign && selectedCampaign.status === 'active' && (
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-[#052831] text-xl font-bold">Submit an AI Use Case Idea</h2>
            <p className="text-slate-500 text-sm mt-1">Fill out the survey to submit your idea to the innovation board.</p>
          </div>
          <div className="p-6">
            <IdeationSurveyForm
              onSubmit={handleSubmitIdea}
              onCancel={() => setShowSurvey(false)}
              submitting={submitting}
            />
          </div>
        </section>
      )}

      {/* Stats Row */}
      {filteredSubmissions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-1">
            <p className="text-slate-500 text-sm font-medium">Total Submissions</p>
            <div className="flex items-center justify-between">
              <p className="text-[#052831] text-3xl font-bold">{totalSubs}</p>
              <TrendingUp size={18} className="text-emerald-400" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-1">
            <p className="text-slate-500 text-sm font-medium">Quick Wins</p>
            <div className="flex items-center justify-between">
              <p className="text-[#052831] text-3xl font-bold">{priorityCounts['Quick Win'] || 0}</p>
              <div className="w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-1">
            <p className="text-slate-500 text-sm font-medium">Strategic Initiatives</p>
            <div className="flex items-center justify-between">
              <p className="text-[#052831] text-3xl font-bold">{priorityCounts['Strategic Initiative'] || 0}</p>
              <div className="w-3 h-3 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-1">
            <p className="text-slate-500 text-sm font-medium">Avg. Composite Score</p>
            <div className="flex items-center justify-between">
              <p className="text-[#052831] text-3xl font-bold">{avgComposite}</p>
              <BarChart3 size={18} className="text-slate-300" />
            </div>
          </div>
        </div>
      )}

      {/* Bubble Chart */}
      {filteredSubmissions.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h2 className="text-[#052831] text-xl font-bold">Impact vs Complexity Matrix</h2>
              <p className="text-slate-400 text-sm font-medium mt-0.5">Bubble size represents viability. Click a bubble to highlight in the table.</p>
            </div>
            <div className="flex items-center gap-4">
              {Object.entries(PRIORITY_STYLES).map(([label, style]) => (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${style.dot}`} />
                  <span className="text-xs font-medium text-slate-500">{label.replace('Revisit Later', 'Revisit')}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-6">
            <IdeationBubbleChart
              submissions={filteredSubmissions}
              selectedId={chartSelectedId}
              onSelect={setChartSelectedId}
            />
          </div>

          {/* Priority Distribution Bar */}
          {totalSubs > 0 && (
            <div className="px-6 pb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[#052831] font-bold text-sm">Priority Distribution</h3>
                <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">{totalSubs} Total</span>
              </div>
              <div className="w-full h-7 bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                {(['Quick Win', 'Strategic Initiative', 'Revisit Later', 'Deprioritise'] as const).map(label => {
                  const count = priorityCounts[label] || 0;
                  if (count === 0) return null;
                  const pct = (count / totalSubs) * 100;
                  return (
                    <div
                      key={label}
                      className={`h-full ${PRIORITY_BAR_COLORS[label]} flex items-center justify-center text-white text-[10px] font-black tracking-tighter transition-all hover:brightness-110`}
                      style={{ width: `${pct}%` }}
                      title={`${label}: ${count}`}
                    >
                      {pct >= 10 ? count : ''}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 grid grid-cols-4 gap-4">
                {(['Quick Win', 'Strategic Initiative', 'Revisit Later', 'Deprioritise'] as const).map(label => {
                  const count = priorityCounts[label] || 0;
                  const pct = totalSubs > 0 ? Math.round((count / totalSubs) * 100) : 0;
                  return (
                    <div key={label} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${PRIORITY_STYLES[label]?.dot || 'bg-slate-300'}`} />
                      <span className="text-xs text-slate-500">
                        <span className="font-bold text-slate-700">{label.replace('Revisit Later', 'Revisit')}:</span> {pct}% ({count})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Submissions Table */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h2 className="text-[#052831] text-xl font-bold">
            Submissions
            {selectedCampaign && <span className="text-sm font-normal text-slate-500 ml-2">— {selectedCampaign.name}</span>}
          </h2>
        </div>
        {filteredSubmissions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <Lightbulb size={24} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No submissions yet. Share the campaign link or submit an idea above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[10px] font-bold uppercase tracking-widest border-b border-slate-100">
                  <th className="px-6 py-4">Problem</th>
                  <th className="px-6 py-4">Submitter</th>
                  <th className="px-6 py-4">Priority</th>
                  <th className="px-6 py-4 text-center">Impact</th>
                  <th className="px-6 py-4 text-center">Complex.</th>
                  <th className="px-6 py-4 text-center">Viable</th>
                  <th className="px-6 py-4 text-right">Date</th>
                  <th className="px-6 py-4 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map(sub => {
                  const isExpanded = expandedSubmissionId === sub.submissionId;
                  const isHighlighted = chartSelectedId === sub.submissionId;
                  const style = PRIORITY_STYLES[sub.priorityLabel] || PRIORITY_STYLES['Revisit Later'];
                  return (
                    <React.Fragment key={sub.submissionId}>
                      <tr
                        onClick={() => setExpandedSubmissionId(isExpanded ? null : sub.submissionId)}
                        className={`cursor-pointer transition-colors border-b border-slate-100 ${
                          isHighlighted ? 'bg-primary/5' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
                            <span className="font-semibold text-slate-800 text-sm truncate max-w-[250px]">{(sub.answers?.q1 || 'No description').substring(0, 60)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{sub.submitterName || sub.submitterEmail || '—'}</td>
                        <td className="px-6 py-4">
                          <span className={`${style.bg} ${style.text} px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider`}>
                            {sub.priorityLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 text-center font-medium">{SCORE_LABEL[sub.impactScore]}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 text-center font-medium">{SCORE_LABEL[sub.complexityScore]}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 text-center font-medium">{SCORE_LABEL[sub.viabilityScore]}</td>
                        <td className="px-6 py-4 text-sm text-slate-500 text-right">
                          {new Date(sub.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={e => { e.stopPropagation(); handleDeleteSubmission(sub.submissionId); }}
                            className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                          <td className="px-10 py-8" colSpan={8}>
                            <div className="grid grid-cols-2 gap-8">
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Problem Statement</h4>
                                  <p className="text-sm text-slate-700 leading-relaxed">{sub.answers?.q1 || '—'}</p>
                                </div>
                                <div>
                                  <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Current Process</h4>
                                  <p className="text-sm text-slate-700 leading-relaxed">{sub.answers?.q2 || '—'}</p>
                                </div>
                                <div>
                                  <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Frequency</h4>
                                  <p className="text-sm text-slate-700">{sub.answers?.q3 || '—'}</p>
                                </div>
                              </div>
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">AI Solution Idea</h4>
                                  <p className="text-sm text-slate-700 leading-relaxed">{sub.answers?.q13 || '—'}</p>
                                </div>
                                <div>
                                  <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Expected Benefits</h4>
                                  <p className="text-sm text-slate-700">{Array.isArray(sub.answers?.q6) ? sub.answers.q6.join(', ') : sub.answers?.q6 || '—'}</p>
                                </div>
                                <div className="flex gap-8">
                                  <div>
                                    <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Source</h4>
                                    <p className="text-sm text-slate-700">{sub.source === 'portal' ? 'Portal' : 'External Form'}</p>
                                  </div>
                                  <div>
                                    <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Composite</h4>
                                    <p className="text-sm text-slate-700 font-mono font-bold">{sub.compositeScore}</p>
                                  </div>
                                  <div>
                                    <h4 className="text-[10px] font-bold uppercase text-primary tracking-widest mb-1">Time / Week</h4>
                                    <p className="text-sm text-slate-700">{sub.answers?.q5 || '—'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="px-6 py-4 bg-slate-50 flex justify-between items-center text-sm border-t border-slate-100">
              <span className="text-slate-500">Showing {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
